/* eslint-disable max-len */
import React, {Component, PropTypes} from 'react';
import Immutable from 'immutable';
import {findDOMNode} from 'react-dom';
import {getDOMNode} from 'utils';
import {defaults as defaultOptions} from 'store/config/reducer';
import {getDataAttrs} from './utils';

export default class Video extends Component {
    static propTypes = {
        id: PropTypes.string,
        width: PropTypes.number,
        height: PropTypes.number,
        export: PropTypes.bool,
        options: PropTypes.object,
        onVideoWrapped: PropTypes.func,
        provider: PropTypes.string,
        panopto_host: PropTypes.string,
        playerId: PropTypes.string,
        accountId: PropTypes.string,
        formats: PropTypes.object,
    };

    static defaultProps = {
        options: Immutable.fromJS({}),
    };

    componentWillMount() {
        const ratio = 0.5625;
        this.width = this.props.width;
        this.height = this.props.height || this.props.width * ratio;
        this.dataAttrs = getDataAttrs(this.props.options, defaultOptions).toJS();
    }

    async componentDidMount() {
        const node = findDOMNode(this);

        // Need to create <script> this way because if rendered by React it doesn't load
        if (this.scriptUrl) {
            const script = document.createElement('script');
            script.src = this.scriptUrl;
            if (this.scriptId) script.id = this.scriptId;
            Object.keys(this.dataAttrs).forEach(key => script.setAttribute(key, this.dataAttrs[key]));
            node.appendChild(script);
        }

        const videojsStylesheetUrl = '//vjs.zencdn.net/5.16.0/video-js.css';
        const videojsStylesheet = getDOMNode(`link[href="${videojsStylesheetUrl}"]`);
        if (this.props.provider === 'videojs') {
            // Load VideoJS stylesheet if not present
            if (!videojsStylesheet) {
                const link = document.createElement('link');
                link.rel = 'stylesheet';
                link.href = videojsStylesheetUrl;
                document.head.appendChild(link);
            }
            // Manually initialize VideoJS if creating <video> with JS
            window.videojs(this.props.id);
        } else if (videojsStylesheet) {
            // Remove VideoJS styles to avoid conflicts with Brightcove
            videojsStylesheet.remove();
        }

        // console.clear(); // eslint-disable-line

        // Re-initialize the wrapper with the new video
        const getVideoMetadata = await window.cieloWrap()[0]; // We only have one video at a time

        if (typeof getVideoMetadata === 'function') {
            const videoMetadata = await getVideoMetadata(this.props.metadata);
            this.props.onVideoWrapped(videoMetadata);
        }
    }

    componentWillUnmount() {
        if (this.props.provider === 'videojs' && window.videojs) {
            window.videojs(this.props.id).dispose();
        }
    }

    renderVideo() {
        const sizeStyle = {width: this.width, height: this.height};
        switch (this.props.provider) {
            case 'youtube':
                const querystring = 'enablejsapi=1&playsinline=1&showinfo=0&rel=0&iv_load_policy=3&color=white';
                return (
                    <iframe
                        src={`//www.youtube.com/embed/${this.props.id}?${querystring}`}
                        width={this.width}
                        height={this.height}
                        allowFullScreen={true}
                        frameBorder={0}
                        {...this.dataAttrs}
                    />
                );

            case 'vimeo':
                return (
                    <iframe
                        src={`//player.vimeo.com/video/${this.props.id}`}
                        width={this.width}
                        height={this.height}
                        allowFullScreen={true}
                        frameBorder={0}
                        {...this.dataAttrs}
                    />
                );

            case 'kaltura':
                this.scriptUrl = [
                    '//cdnapisec.kaltura.com',
                    `/p/${this.props.accountId}/sp/${this.props.accountId}00/embedIframeJs/uiconf_id/${this.props.playerId}/partner_id/${this.props.accountId}`,
                    '?autoembed=true',
                    `&entry_id=${this.props.id}`,
                    '&playerId=kaltura_player',
                    `&width=${this.width}`,
                    `&height=${this.height}`,
                ].join('');
                return <div id='kaltura_player' style={sizeStyle} />;

            case 'jwplayer':
                const embedId = `${this.props.id}-${this.props.playerId}`;
                this.scriptUrl = `//content.jwplatform.com/players/${embedId}.js`;
                return <div id={`botr_${embedId.replace('-', '_')}_div`} style={sizeStyle} />;

            case 'videojs':
                return (
                    <video
                        className='video-js'
                        preload='auto'
                        controls={true}
                        style={sizeStyle}
                        id={this.props.id}
                        {...this.dataAttrs}
                    >
                        {Object
                            .entries(this.props.formats)
                            .map(([type, src]) => (
                                <source key={type} type={`video/${type}`} src={src} />
                            ))
                        }
                    </video>
                );

            case 'brightcove':
                this.scriptUrl = `//players.brightcove.net/${this.props.accountId}/${this.props.playerId}_default/index.min.js`;
                return (
                    <video
                        className='video-js'
                        data-video-id={this.props.id}
                        data-account={this.props.accountId}
                        data-player={this.props.playerId}
                        data-embed='default'
                        controls={true}
                        style={sizeStyle}
                        {...this.dataAttrs}
                    />
                );

            case 'mediasite':
                return (
                    <iframe
                        id={this.props.id}
                        width={this.width}
                        height={this.height}
                        allowFullScreen={true}
                        frameBorder={0}
                        src={`//demo7.mediasite.com/Mediasite/Play/${this.props.id}`}
                        {...this.dataAttrs}
                    />
                );

            case 'vidyard':
                this.scriptId = `vidyard_embed_code_${this.props.id}`;
                this.scriptUrl = `//play-staging.vidyard.com/${this.props.id}.js?v=3.1.1&type=inline`;

            case 'panopto':
                /*
                This is a workaround to make the Panopto embeded iFrame work.
                Panopto embeded iFrame shoule be initiated by Panopto API to make its controls work.
                Creating Panopto embeded iFrame didn't work here, so we add a placeholder to pass the
                necessary arguments to the actual iFrame, which will be created in Panoptp adapter
                script. Then, this placeholder will be deleted.
                */
                return (
                    <div
                    src={`https://${this.props.panopto_host}.hosted.panopto.com/Panopto/Pages/Embed.aspx?id=${this.props.id}`}
                    width={this.width}
                    height={this.height}
                    allowFullScreen={true}
                    frameBorder={0}
                    data-panopto-host={this.props.panopto_host}
                    hidden
                    {...this.dataAttrs}></div>
                );
        }
    }

    render() {
        const video = this.renderVideo();

        if (this.props.export) {
            return (
                <div className={[this.props.provider, this.props.id].filter(Boolean).join('-')}>
                    {video}
                    {this.scriptUrl && (
                        <script
                            type='text/javascript'
                            id={this.scriptId || ''}
                            src={this.scriptUrl}
                        />
                    )}
                </div>
            );
        }

        return <div id='video' className='video'>{video}</div>;
    }
}



// WEBPACK FOOTER //
// ./scripts/test-page/video.js